import { useTranslation } from 'react-i18next';
import {
  Mail,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Clock,
  FileText,
} from 'lucide-react';
import { usePatientEmailHistory } from '@/hooks/usePatientEmailHistory';
import { usePatient } from '@/hooks/usePatients';

interface EmailHistoryTabProps {
  clinicId: string;
  patientId: string;
}

function formatTime(ts: string | null | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  const day = d.toLocaleDateString('da-DK', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric' 
  });
  const time = d.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
  return `${day} kl. ${time}`;
}

function StatusPill({ status }: { status: 'sent' | 'failed' }) {
  const { t } = useTranslation();
  switch (status) {
    case 'sent':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-950/30 ring-1 ring-emerald-200/70 dark:ring-emerald-900 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 className="h-3 w-3" />
          {t('email.status_sent', 'Sendt')}
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 dark:bg-rose-950/30 ring-1 ring-rose-200/70 dark:ring-rose-900 px-2 py-0.5 text-[11px] font-medium text-rose-700 dark:text-rose-300">
          <AlertTriangle className="h-3 w-3" />
          {t('email.status_failed', 'Fejlet')}
        </span>
      );
  }
}

export function EmailHistoryTab({ clinicId, patientId }: EmailHistoryTabProps) {
  const { t } = useTranslation();
  const { data: patient, isLoading: patientLoading } = usePatient(patientId);
  const { data: emails = [], isLoading: emailsLoading } = usePatientEmailHistory(clinicId, patientId);

  const patientEmail = patient?.email ?? '';

  if (patientLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <section className="space-y-6">
      {/* Header */}
      <div className="rounded-2xl bg-white/80 dark:bg-gray-900/60 backdrop-blur ring-1 ring-gray-200/70 dark:ring-gray-800/70 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center justify-center h-10 w-10 rounded-2xl bg-indigo-600/10 ring-1 ring-indigo-600/20 text-indigo-600 dark:text-indigo-300 flex-shrink-0">
              <Mail className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {t('email.history', 'Email-historik')}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {patientEmail ? (
                  patientEmail
                ) : (
                  <span className="text-rose-600">
                    {t('email.no_email_registered', 'Ingen email registreret')}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs">
              <span className="text-gray-800 dark:text-gray-200 font-medium">
                {emails.length}
              </span>
              <span className="text-gray-600 dark:text-gray-400 ml-1">
                {emails.length === 1 ? t('email.email_singular', 'email') : t('email.emails_plural', 'emails')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Email list */}
      <div className="rounded-2xl bg-white/80 dark:bg-gray-900/60 backdrop-blur ring-1 ring-gray-200/70 dark:ring-gray-800/70">
        {emailsLoading ? (
          <div className="p-12 text-center text-sm text-gray-500 dark:text-gray-400 inline-flex items-center justify-center gap-2 w-full">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('email.loading', 'Indlæser email-historik…')}
          </div>
        ) : emails.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="h-8 w-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('email.empty', 'Ingen emails endnu')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('email.empty_hint', 'Når en faktura sendes til denne patient, dukker den op her.')}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100/80 dark:divide-gray-800/60">
            {emails.map((email) => (
              <li key={email.id} className="px-5 py-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <StatusPill status={email.status} />
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950/30 ring-1 ring-indigo-200/70 dark:ring-indigo-900 text-indigo-700 dark:text-indigo-300 rounded text-[11px] font-medium">
                        <FileText className="h-3 w-3" />
                        {t('email.type_invoice', 'Faktura')}
                      </span>
                      {email.invoice_number && (
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                          #{email.invoice_number}
                        </span>
                      )}
                    </div>

                    <div className="text-sm font-medium text-gray-900 dark:text-gray-200 mb-1">
                      {email.subject}
                    </div>

                    <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>
                          {t('email.sent_at', 'Sendt')}: {formatTime(email.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        <span>
                          {t('email.sent_to', 'Til')}: {email.sent_to}
                        </span>
                      </div>
                    </div>

                    {email.error_message && (
                      <div className="mt-2 p-2 rounded bg-rose-50 dark:bg-rose-950/30 ring-1 ring-rose-200/70 dark:ring-rose-900">
                        <div className="text-[11px] text-rose-700 dark:text-rose-300 font-medium mb-0.5">
                          {t('email.error', 'Fejl')}:
                        </div>
                        <div className="text-[11px] text-rose-600 dark:text-rose-400">
                          {email.error_message}
                        </div>
                      </div>
                    )}

                    {email.postmark_message_id && (
                      <div className="mt-1 text-[10px] font-mono text-gray-400">
                        ID: {email.postmark_message_id}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <div className="text-right text-[11px] text-gray-500 dark:text-gray-400">
                      {formatTime(email.created_at)}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Info notice */}
      <div className="rounded-xl bg-gray-50/80 dark:bg-gray-900/40 ring-1 ring-gray-200/70 dark:ring-gray-800/70 p-4">
        <p className="text-xs text-gray-600 dark:text-gray-400">
          {t(
            'email.info_invoice_only',
            'Denne historik viser kun faktura-emails. Andre email-typer (påmindelser, bekræftelser) er ikke inkluderet endnu.',
          )}
        </p>
      </div>
    </section>
  );
}
